import { useState, useEffect } from "react";
import { Box, Tabs, Tab, Button } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { MuiHelper } from "@/helpers";
import { useDataApi } from "@/hooks";
import { baseUrl } from "@/consts";
import { formatDateToPST, isNonEmptyArray } from "@/utils";

// Product Audit columns
const productAuditColumns: GridColDef[] = [
  { field: "productAuditId", headerName: "ID", width: 70 },
  { field: "productId", headerName: "PRODUCT ID", width: 100 },
  { field: "auditAction", headerName: "ACTION", width: 100 },
  { field: "newSKU", headerName: "SKU", width: 180 },
  { field: "newProductDescription", headerName: "PRODUCT", width: 250 },
  {
    field: "oldInventoryCount",
    headerName: "OLD COUNT",
    type: "number",
    width: 100,
    valueFormatter: (value: number | null) => value ?? "-",
  },
  {
    field: "newInventoryCount",
    headerName: "NEW COUNT",
    type: "number",
    width: 100,
    valueFormatter: (value: number | null) => value ?? "-",
  },
  {
    field: "oldPrice",
    headerName: "OLD PRICE",
    type: "number",
    width: 100,
    valueFormatter: (value: number | null) =>
      value != null ? `$${value.toFixed(2)}` : "-",
  },
  {
    field: "newPrice",
    headerName: "NEW PRICE",
    type: "number",
    width: 100,
    valueFormatter: (value: number | null) =>
      value != null ? `$${value.toFixed(2)}` : "-",
  },
  { field: "changedBy", headerName: "CHANGED BY", width: 200 },
  {
    field: "changedOn",
    headerName: "CHANGED ON",
    width: 200,
    renderCell: (params: any) => {
      const raw = params?.value ?? params?.row?.changedOn;
      const formatted = formatDateToPST(raw);
      if (formatted) return formatted;

      try {
        const d = raw ? new Date(raw) : undefined;
        if (d && !isNaN(d.getTime())) return d.toLocaleString();
      } catch (e) {
        /* fallthrough */
      }

      return raw ?? "";
    },
  },
];

const History: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const {
    data: productAuditData,
    setUrl: setProductAuditUrl,
    isLoading: isProductAuditLoading,
  } = useDataApi();

  // Fetch product audit data when tab is selected
  useEffect(() => {
    if (tabIndex === 0) {
      setProductAuditUrl(baseUrl.PRODUCT_AUDIT);
    }
  }, [tabIndex, setProductAuditUrl]);

  const refreshProductAuditData = () => {
    setProductAuditUrl(`${baseUrl.PRODUCT_AUDIT}?t=${Date.now()}`);
  };

  return (
    <Box>
      <Box>{MuiHelper.getHeader("History")}</Box>
      <Box
        sx={{
          padding: "0 28px 0 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Product Audit" />
          {/* Add more tabs here in the future */}
        </Tabs>
        {tabIndex === 0 && (
          <Button onClick={refreshProductAuditData} sx={{ marginLeft: 2 }}>
            Refresh
          </Button>
        )}
      </Box>

      {tabIndex === 0 && (
        <Box sx={{ padding: "0 28px 0 40px", height: 600 }}>
          <DataGrid
            rows={isNonEmptyArray(productAuditData) ? productAuditData : []}
            columns={productAuditColumns}
            getRowId={(row) => row.productAuditId}
            loading={isProductAuditLoading}
            autoHeight={false}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              sorting: {
                sortModel: [{ field: "changedOn", sort: "desc" }],
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default History;
